// shared/src/news/news-items-store.ts
// D1 store for the append-only `news_items` table (migrations/0082_news_items.sql).
// F+G Phase 1 (docs/news-feed-tab-and-preprocessing-2026-07-11-design.md §5): one
// queryable store of every news item we fetched — RSS (refresh-worker, run through
// dedup/rank) + search-news (chain macro + p-stage ticker, the already-ranked items
// that were previously DISCARDED after prompt render). Powers the /oracle dash News tab
// (news consolidated there in P2c) and a historical feed. NO brief-behaviour change — this only
// PERSISTS the items the pipeline already computed.
//
// Writer + reader share this one module so the INSERT/SELECT column list lives in a
// single place (mirrors news-impact-store.ts / write-log.ts). Structural D1 subset so
// shared/ does not depend on @cloudflare/workers-types.
//
// Idempotency: `id` is a DETERMINISTIC FNV-1a of the LOGICAL item identity —
// pipeline|scope|normalizedUrl|published_at|title — written with INSERT OR IGNORE.
// The identity deliberately EXCLUDES the fetch run (refresh_id / brief_id): the same
// article re-fetched by a later 15-min refresh cron (or a later brief) hashes to the
// SAME id, so it collapses to one row instead of accumulating a fresh copy per cron
// (the 2026-07-11 duplication bug: refresh_id in the key made every re-fetch a new
// row → the store grew ~96×/day + the News tab showed each item once per cron). The
// run that FIRST saw the item stamps refresh_id / brief_id as provenance metadata;
// INSERT OR IGNORE keeps that first-writer value. A step.do retry within a run is
// still idempotent (all fields identical), so retry-safety is unchanged.

import { z } from 'zod';
import type { NormalizedNewsItem } from './types.js';
import { normalizeUrl } from './dedup.js';
import { tagEntityTickers, tagThemes, computeSalience } from './tag.js';

// Structural D1 subset (prepare → bind → batch/all) — same pattern as
// news-impact-store.ts / write-log.ts.
export interface NewsItemsD1 {
  prepare(sql: string): NewsItemsD1Stmt;
  batch(statements: NewsItemsD1Stmt[]): Promise<unknown[]>;
}
export interface NewsItemsD1Stmt {
  bind(...values: unknown[]): NewsItemsD1Stmt;
  all<T = unknown>(): Promise<{ results: T[]; success: boolean }>;
  run(): Promise<unknown>;
}

/** Which pipeline produced the row. */
export type NewsItemPipeline = 'rss' | 'search';

/** The raw DB row. Mirrors the 0082 DDL columns exactly (schema-vs-ddl test). */
export interface NewsItemRow {
  readonly id:               string;
  readonly fetched_at:       string;          // ISO-Z fetch time (retention key)
  readonly pipeline:         NewsItemPipeline;
  readonly source_id:        string;          // feed / publisher identifier (= source_domain)
  readonly scope:            string;          // rss bucket | ticker | 'macro'
  readonly title:            string;
  readonly url:              string;
  readonly source_domain:    string;
  readonly published_at:     string;          // ISO 8601 ('' when the feed omitted it)
  readonly tier:             'A' | 'B' | 'C';
  readonly cross_vendor_hit: number;          // 0 | 1
  readonly deduped_out:      string | null;   // reserved: winner url (Phase 1 stores survivors → NULL)
  readonly topic:            string | null;   // NULL until the Phase 2 classifier backfills it
  readonly brief_id:         string | null;   // set for pipeline='search'; NULL for rss
  readonly refresh_id:       string | null;   // set for pipeline='rss'; NULL for search
  readonly created_at:       string;          // write time
  readonly summary:          string | null;   // vendor summary/description (0103); NULL when absent
  // Central news pool P0 (0120) — intrinsic tags computed at ingest by tag.ts. entity_tickers /
  // themes are JSON arrays stored as TEXT; salience is a categorical 0..1 REAL. All NULL for
  // rows written before 0120 until the lazy-on-read backfill tags them.
  readonly entity_tickers:   string | null;   // JSON array of canonical tickers, e.g. '["$BTC"]'
  readonly themes:           string | null;   // JSON array of macro themes, e.g. '["fed_rates"]'
  readonly salience:         number | null;   // 0..1 server-side ranking score; NEVER a number to the LLM
}

/** Zod schema for a DB read → validated at the api boundary before it crosses to
 *  the wire (C035). */
export const NewsItemRowSchema = z.object({
  id:               z.string(),
  fetched_at:       z.string(),
  pipeline:         z.enum(['rss', 'search']),
  source_id:        z.string(),
  scope:            z.string(),
  title:            z.string(),
  url:              z.string(),
  source_domain:    z.string(),
  published_at:     z.string(),
  tier:             z.enum(['A', 'B', 'C']),
  cross_vendor_hit: z.number().int().min(0).max(1),
  deduped_out:      z.string().nullable(),
  topic:            z.string().nullable(),
  brief_id:         z.string().nullable(),
  refresh_id:       z.string().nullable(),
  created_at:       z.string(),
  summary:          z.string().nullable(),
  entity_tickers:   z.string().nullable(),
  themes:           z.string().nullable(),
  salience:         z.number().nullable(),
});

const COLS = 'id, fetched_at, pipeline, source_id, scope, title, url, source_domain, published_at, tier, cross_vendor_hit, deduped_out, topic, brief_id, refresh_id, created_at, summary, entity_tickers, themes, salience';
const INSERT_SQL = `INSERT OR IGNORE INTO news_items (${COLS}) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`;

/** FNV-1a 32-bit → 8-hex. Deterministic, no crypto import (shared runs everywhere). */
function fnv1a(s: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Deterministic id — same LOGICAL item (same pipeline/scope/url/date/title) always
 *  hashes to the same id regardless of WHICH run fetched it, so INSERT OR IGNORE makes
 *  both intra-run retries AND cross-cron re-fetches idempotent. The fetch run
 *  (refresh_id / brief_id) is intentionally NOT part of the identity — see the
 *  module header for the duplication bug this prevents. */
export function makeNewsItemId(
  pipeline: NewsItemPipeline,
  scope:    string,
  url:      string,
  publishedAt: string,
  title:    string,
): string {
  const key = `${pipeline}|${scope}|${normalizeUrl(url)}|${publishedAt}|${title.trim().toLowerCase()}`;
  return `ni_${fnv1a(key)}${fnv1a(key.split('').reverse().join(''))}`;
}

/** Context for one batch of rows (all share pipeline/scope/owner/timestamps). */
export interface NewsItemWriteContext {
  readonly pipeline:   NewsItemPipeline;
  readonly scope:      string;
  readonly fetchedAt:  string;          // ISO-Z
  readonly createdAt:  string;          // ISO-Z
  readonly briefId:    string | null;   // set for 'search'
  readonly refreshId:  string | null;   // set for 'rss'
}

/** Map a ranked NormalizedNewsItem → a news_items row. `source_id` = source_domain
 *  (Phase 1 groups the F tab by publisher; feed-level grouping is a Phase-3 refinement). */
export function normalizedToNewsItemRow(n: NormalizedNewsItem, ctx: NewsItemWriteContext): NewsItemRow {
  // Intrinsic tags (0120) — deterministic, $0, computed over title + summary at ingest.
  const text = `${n.title} ${n.summary ?? ''}`;
  const nowMs = Date.parse(ctx.createdAt);
  return {
    id:               makeNewsItemId(ctx.pipeline, ctx.scope, n.url, n.published_at_iso, n.title),
    fetched_at:       ctx.fetchedAt,
    pipeline:         ctx.pipeline,
    source_id:        n.source_domain,
    scope:            ctx.scope,
    title:            n.title,
    url:              n.url,
    source_domain:    n.source_domain,
    published_at:     n.published_at_iso,
    tier:             n.source_tier,
    cross_vendor_hit: n.cross_vendor_hit ? 1 : 0,
    deduped_out:      null,
    topic:            null,
    brief_id:         ctx.briefId,
    refresh_id:       ctx.refreshId,
    created_at:       ctx.createdAt,
    summary:          n.summary || null,   // '' (GDELT / no vendor summary) persists as NULL
    entity_tickers:   JSON.stringify(tagEntityTickers(text)),
    themes:           JSON.stringify(tagThemes(text)),
    salience:         computeSalience(n.source_tier, !!n.cross_vendor_hit, n.published_at_iso, Number.isFinite(nowMs) ? nowMs : 0),
  };
}

/** Recompute + persist tags for the given text per row — the version-bump / lazy-on-read path
 *  (mirrors updateNewsItemTopics). Idempotent (re-running over the same ids re-sets the same
 *  deterministic values). No-op on empty. */
export async function updateNewsItemTags(
  db:      NewsItemsD1,
  updates: ReadonlyArray<{ id: string; entity_tickers: string; themes: string; salience: number }>,
): Promise<void> {
  if (updates.length === 0) return;
  const stmt = db.prepare('UPDATE news_items SET entity_tickers = ?, themes = ?, salience = ? WHERE id = ?');
  await db.batch(updates.map((u) => stmt.bind(u.entity_tickers, u.themes, u.salience, u.id)));
}

/** Append rows (idempotent INSERT OR IGNORE, batched). No-op on empty. Callers wrap
 *  best-effort — a news_items write failure must NEVER fail a brief or a refresh. */
export async function writeNewsItems(db: NewsItemsD1, rows: ReadonlyArray<NewsItemRow>): Promise<void> {
  if (rows.length === 0) return;
  const stmt = db.prepare(INSERT_SQL);
  const inserts = rows.map((r) => stmt.bind(
    r.id, r.fetched_at, r.pipeline, r.source_id, r.scope, r.title, r.url, r.source_domain,
    r.published_at, r.tier, r.cross_vendor_hit, r.deduped_out, r.topic, r.brief_id, r.refresh_id, r.created_at, r.summary,
    r.entity_tickers, r.themes, r.salience,
  ));
  await db.batch(inserts);
}

/** Read recent news_items for the /oracle dash News tab (buildNewsOracleBundle). Newest first,
 *  bounded by a fetched_at floor + a row cap. */
export async function readRecentNewsItems(
  db:       NewsItemsD1,
  sinceIso: string,
  limit:    number,
): Promise<NewsItemRow[]> {
  const res = await db
    .prepare(`SELECT ${COLS} FROM news_items WHERE fetched_at >= ? ORDER BY fetched_at DESC, source_domain ASC LIMIT ?`)
    .bind(sinceIso, limit)
    .all<NewsItemRow>();
  return res.results ?? [];
}

/** Rows still awaiting an asset-class tag (Phase 2 classify backfill). topic IS NULL,
 *  newest first, capped. Only the fields the classifier reads are selected. */
export async function readUnclassifiedNewsItems(
  db:    NewsItemsD1,
  limit: number,
): Promise<Array<{ id: string; title: string; summary: string; source_domain: string }>> {
  // summary is now a real column (0103); NULL (older rows / GDELT) maps back to ''.
  const res = await db
    .prepare('SELECT id, title, summary, source_domain FROM news_items WHERE topic IS NULL ORDER BY fetched_at DESC LIMIT ?')
    .bind(limit)
    .all<{ id: string; title: string; summary: string | null; source_domain: string }>();
  return (res.results ?? []).map((r) => ({ ...r, summary: r.summary ?? '' }));
}

/** Batch-write asset-class topics from the classify backfill (id → topic). Idempotent
 *  (a re-run over the same ids just re-sets the same value). No-op on empty. */
export async function updateNewsItemTopics(
  db:      NewsItemsD1,
  updates: ReadonlyArray<{ id: string; topic: string }>,
): Promise<void> {
  if (updates.length === 0) return;
  const stmt = db.prepare('UPDATE news_items SET topic = ? WHERE id = ?');
  await db.batch(updates.map((u) => stmt.bind(u.topic, u.id)));
}
